import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as signalR from '@microsoft/signalr';
import Swal from 'sweetalert2';
import { baseUrl } from '../../constants';
import { AuthService } from '../../core/auth.service';
import { MeetingsService } from './meetings.service';

interface RoomPeer {
  connectionId: string;
  userId?: string;
  displayName: string;
  stream?: MediaStream;
}

interface MeetingChatMessage {
  id: number;
  meetingId: number;
  message: string;
  createdByUserId: string;
  createdByName: string;
  createdAt: string;
}

interface PresentationState {
  connectionId: string;
  userId?: string;
  displayName: string;
  isPresenting: boolean;
}

@Component({
  selector: 'app-meeting-room',
  templateUrl: './meeting-room.component.html',
  styleUrls: ['./meeting-room.component.scss']
})
export class MeetingRoomComponent implements OnInit, OnDestroy {
  readonly audioFirstThreshold = 6;
  @ViewChild('localMedia') localMedia?: ElementRef<HTMLMediaElement>;
  @ViewChild('presentationMedia') presentationMedia?: ElementRef<HTMLMediaElement>;
  @ViewChild('chatStream') chatStream?: ElementRef<HTMLDivElement>;

  loading = true;
  joining = false;
  meeting: any;
  connection?: signalR.HubConnection;
  localStream?: MediaStream;
  peers = new Map<string, RTCPeerConnection>();
  participants: RoomPeer[] = [];
  micEnabled = true;
  cameraEnabled = true;
  sharingScreen = false;
  mediaError = '';
  chatMessages: MeetingChatMessage[] = [];
  chatDraft = '';
  chatSending = false;
  activePresentation: PresentationState | null = null;
  private screenStream?: MediaStream;
  private cameraTrack?: MediaStreamTrack;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private meetingsService: MeetingsService,
    private auth: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    const meetingId = Number(this.route.snapshot.paramMap.get('id'));
    if (!meetingId) {
      this.router.navigateByUrl('/meetings');
      return;
    }

    this.meetingsService.getById(meetingId).subscribe({
      next: async (meeting) => {
        this.meeting = meeting;
        this.chatMessages = Array.isArray(meeting?.chatMessages) ? [...meeting.chatMessages] : [];
        this.loading = false;
        await this.joinMeetingRoom();
      },
      error: async (err) => {
        this.loading = false;
        await Swal.fire('Error', err?.error?.message || 'Failed to load meeting room.', 'error');
        this.router.navigateByUrl('/meetings');
      }
    });
  }

  async ngOnDestroy(): Promise<void> {
    await this.leaveRoom(false);
  }

  get isVideoRoom(): boolean {
    return String(this.meeting?.roomType || '').toLowerCase() !== 'audio';
  }

  get currentUserName(): string {
    return this.auth.getUser()?.fullName || this.auth.getUser()?.email || 'Participant';
  }

  get attendeeCount(): number {
    return Number(this.meeting?.attendeeCount || this.meeting?.attendees?.length || 0);
  }

  get shouldStartCameraOff(): boolean {
    return this.isVideoRoom && this.attendeeCount > this.audioFirstThreshold;
  }

  get canUseBrowserMedia(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  }

  get canShareScreen(): boolean {
    return this.isVideoRoom && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
  }

  get localParticipantConnectionId(): string {
    return this.connection?.connectionId || '';
  }

  get activePresentationIsLocal(): boolean {
    return !!this.activePresentation?.isPresenting && this.activePresentation.connectionId === this.localParticipantConnectionId;
  }

  get tileParticipants(): RoomPeer[] {
    if (!this.activePresentation?.isPresenting) {
      return this.participants;
    }

    return this.participants.filter((participant) => participant.connectionId !== this.activePresentation?.connectionId);
  }

  async leaveRoom(navigate = true): Promise<void> {
    try {
      if (this.connection && this.meeting?.id) {
        await this.connection.invoke('LeaveRoom', this.meeting.id);
      }
    } catch {}

    this.peers.forEach((peer) => peer.close());
    this.peers.clear();
    this.participants = [];

    this.stopScreenSharing(false);

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = undefined;
    }

    if (this.connection) {
      await this.connection.stop();
      this.connection = undefined;
    }

    if (navigate) {
      this.router.navigateByUrl('/meetings');
    }
  }

  toggleMic(): void {
    this.micEnabled = !this.micEnabled;
    (this.localStream?.getAudioTracks() || []).forEach((track) => { track.enabled = this.micEnabled; });
  }

  toggleCamera(): void {
    if (this.sharingScreen) {
      return;
    }

    const videoTracks = this.localStream?.getVideoTracks() || [];
    if (!videoTracks.length) return;
    this.cameraEnabled = !this.cameraEnabled;
    videoTracks.forEach((track) => { track.enabled = this.cameraEnabled; });
  }

  async toggleScreenShare(): Promise<void> {
    if (!this.canShareScreen) {
      await Swal.fire('Screen Share Unavailable', 'This browser cannot share screen in the current context. Use a supported desktop browser over HTTPS.', 'warning');
      return;
    }

    if (this.sharingScreen) {
      this.stopScreenSharing();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false
      });
      const screenTrack = stream.getVideoTracks()[0];
      if (!screenTrack) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      screenTrack.onended = () => this.stopScreenSharing();
      this.screenStream = stream;
      this.sharingScreen = true;
      this.cameraEnabled = true;
      this.replaceOutgoingVideoTrack(screenTrack);
      this.attachLocalMedia(this.screenStream);
      await this.connection?.invoke('SetPresentationState', this.meeting.id, true);
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        await Swal.fire('Screen Share Cancelled', 'You cancelled the screen sharing request.', 'info');
        return;
      }

      await Swal.fire('Screen Share Failed', err?.message || 'Failed to start screen sharing.', 'error');
    }
  }

  async copyRoomCode(): Promise<void> {
    await navigator.clipboard?.writeText(this.meeting?.roomCode || '');
  }

  sendChatMessage(): void {
    const message = String(this.chatDraft || '').trim();
    if (!message || !this.connection || !this.meeting?.id || this.chatSending) {
      return;
    }

    this.chatSending = true;
    this.connection.invoke('SendChatMessage', this.meeting.id, message)
      .then(() => {
        this.chatDraft = '';
        this.chatSending = false;
        this.scrollChatToLatest();
      })
      .catch(async (err) => {
        this.chatSending = false;
        await Swal.fire('Chat Failed', err?.message || 'Failed to send chat message.', 'error');
      });
  }

  handleChatKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.sendChatMessage();
  }

  private async joinMeetingRoom(): Promise<void> {
    this.joining = true;
    try {
      if (!this.canUseBrowserMedia) {
        throw new Error('This browser cannot access microphone/camera here. Use HTTPS or open the app on localhost, then allow microphone/camera permission.');
      }

      const constraints = this.isVideoRoom
        ? { audio: true, video: true }
        : { audio: true, video: false };
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.cameraTrack = this.localStream.getVideoTracks()[0];
      if (this.shouldStartCameraOff) {
        this.cameraEnabled = false;
        this.localStream.getVideoTracks().forEach((track) => { track.enabled = false; });
      }
      this.attachLocalMedia(this.localStream);

      const hubUrl = `${baseUrl.replace(/api\/$/i, '')}hubs/meetings`;
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, { accessTokenFactory: () => localStorage.getItem('token') || '' })
        .withAutomaticReconnect()
        .build();

      this.registerHubHandlers();
      await this.connection.start();
      await this.connection.invoke('JoinRoom', this.meeting.id, this.meeting.roomCode, this.currentUserName);
      this.joining = false;
    } catch (err: any) {
      this.joining = false;
      this.mediaError = err?.message || 'Failed to join live meeting room.';
      await Swal.fire('Cannot Join Room', this.mediaError, 'error');
    }
  }

  private registerHubHandlers(): void {
    if (!this.connection) return;

    this.connection.on('ExistingParticipants', async (participants: any[]) => {
      for (const participant of participants || []) {
        await this.ensurePeerConnection(participant, true);
      }
    });

    this.connection.on('ParticipantJoined', (participant: any) => {
      this.upsertParticipant(participant);
    });

    this.connection.on('ParticipantLeft', (connectionId: string) => {
      this.removeParticipant(connectionId);
    });

    this.connection.on('ReceiveOffer', async (fromConnectionId: string, sdp: string, participant: any) => {
      const peer = await this.ensurePeerConnection({ ...participant, connectionId: fromConnectionId }, false);
      await peer.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp }));
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      await this.connection?.invoke('SendAnswer', this.meeting.id, fromConnectionId, answer.sdp);
    });

    this.connection.on('ReceiveAnswer', async (fromConnectionId: string, sdp: string) => {
      const peer = this.peers.get(fromConnectionId);
      if (!peer) return;
      await peer.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }));
    });

    this.connection.on('ReceiveIceCandidate', async (fromConnectionId: string, candidate: string, sdpMid: string | null, sdpMLineIndex: number | null, participant: any) => {
      const peer = this.peers.get(fromConnectionId) || await this.ensurePeerConnection({ ...participant, connectionId: fromConnectionId }, false);
      await peer.addIceCandidate(new RTCIceCandidate({ candidate, sdpMid: sdpMid ?? undefined, sdpMLineIndex: sdpMLineIndex ?? undefined }));
    });

    this.connection.on('ReceiveChatMessage', (message: MeetingChatMessage) => {
      this.chatMessages = [...this.chatMessages, message].sort((left, right) =>
        new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
      this.scrollChatToLatest();
    });

    this.connection.on('PresentationStateChanged', (state: PresentationState) => {
      this.activePresentation = state?.isPresenting ? state : null;
      this.attachPresentationMedia();
    });
  }

  private async ensurePeerConnection(participant: any, shouldCreateOffer: boolean): Promise<RTCPeerConnection> {
    const connectionId = participant.connectionId;
    let peer = this.peers.get(connectionId);
    if (peer) {
      this.upsertParticipant(participant);
      return peer;
    }

    peer = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
    this.peers.set(connectionId, peer);
    this.upsertParticipant(participant);

    this.getOutgoingTracks().forEach((track) => {
      const sourceStream = track.kind === 'video' && this.screenStream ? this.screenStream : this.localStream;
      if (sourceStream) {
        peer?.addTrack(track, sourceStream);
      }
    });

    peer.onicecandidate = async (event) => {
      if (!event.candidate) return;
      await this.connection?.invoke('SendIceCandidate', this.meeting.id, connectionId, event.candidate.candidate, event.candidate.sdpMid, event.candidate.sdpMLineIndex);
    };

    peer.ontrack = (event) => {
      const stream = event.streams[0];
      this.upsertParticipant({ ...participant, connectionId, stream });
      this.attachRemoteMedia(connectionId, stream);
    };

    peer.onconnectionstatechange = () => {
      if (peer && ['disconnected', 'failed', 'closed'].includes(peer.connectionState)) {
        this.removeParticipant(connectionId);
      }
    };

    if (shouldCreateOffer) {
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      await this.connection?.invoke('SendOffer', this.meeting.id, connectionId, offer.sdp);
    }

    return peer;
  }

  private upsertParticipant(participant: any): void {
    const existing = this.participants.find((row) => row.connectionId === participant.connectionId);
    if (existing) {
      existing.displayName = participant.displayName || existing.displayName;
      existing.userId = participant.userId || existing.userId;
      if (participant.stream) existing.stream = participant.stream;
    } else {
      this.participants = [...this.participants, {
        connectionId: participant.connectionId,
        displayName: participant.displayName || 'Participant',
        userId: participant.userId,
        stream: participant.stream
      }];
    }

    if (participant.stream) {
      this.attachRemoteMedia(participant.connectionId, participant.stream);
    }
  }

  private removeParticipant(connectionId: string): void {
    this.peers.get(connectionId)?.close();
    this.peers.delete(connectionId);
    this.participants = this.participants.filter((row) => row.connectionId !== connectionId);
  }

  private attachLocalMedia(stream?: MediaStream): void {
    setTimeout(() => {
      const element = this.localMedia?.nativeElement;
      const activeStream = stream || this.screenStream || this.localStream;
      if (element && activeStream && element.srcObject !== activeStream) {
        element.srcObject = activeStream;
      }
    });
  }

  private attachRemoteMedia(connectionId: string, stream: MediaStream): void {
    setTimeout(() => {
      const elementId = this.isVideoRoom ? `remote-video-${connectionId}` : `remote-audio-${connectionId}`;
      const element = document.getElementById(elementId) as HTMLMediaElement | null;
      if (element && element.srcObject !== stream) {
        element.srcObject = stream;
      }
      if (this.activePresentation?.connectionId === connectionId) {
        this.attachPresentationMedia();
      }
    });
  }

  private replaceOutgoingVideoTrack(track: MediaStreamTrack): void {
    this.peers.forEach((peer) => {
      const sender = peer.getSenders().find((candidate) => candidate.track?.kind === 'video');
      if (sender) {
        sender.replaceTrack(track);
      }
    });
  }

  private getOutgoingTracks(): MediaStreamTrack[] {
    if (!this.localStream) {
      return [];
    }

    const tracks = this.localStream.getAudioTracks();
    const activeVideoTrack = this.screenStream?.getVideoTracks()[0] || this.localStream.getVideoTracks()[0];
    if (activeVideoTrack) {
      tracks.push(activeVideoTrack);
    }

    return tracks;
  }

  private stopScreenSharing(recoverCamera = true): void {
    if (!this.sharingScreen && !this.screenStream) {
      return;
    }

    const screenStream = this.screenStream;
    this.screenStream = undefined;
    this.sharingScreen = false;
    this.connection?.invoke('SetPresentationState', this.meeting.id, false).catch(() => undefined);

    if (screenStream) {
      screenStream.getTracks().forEach((track) => {
        track.onended = null;
        track.stop();
      });
    }

    if (recoverCamera && this.cameraTrack) {
      this.replaceOutgoingVideoTrack(this.cameraTrack);
      this.cameraTrack.enabled = this.cameraEnabled;
      this.attachLocalMedia(this.localStream);
      return;
    }

    this.attachLocalMedia(this.localStream);
    this.attachPresentationMedia();
  }

  private attachPresentationMedia(): void {
    setTimeout(() => {
      const element = this.presentationMedia?.nativeElement;
      if (!element || !this.activePresentation?.isPresenting) {
        return;
      }

      if (this.activePresentationIsLocal) {
        const activeStream = this.screenStream || this.localStream;
        if (activeStream && element.srcObject !== activeStream) {
          element.srcObject = activeStream;
        }
        return;
      }

      const participant = this.participants.find((row) => row.connectionId === this.activePresentation?.connectionId);
      if (participant?.stream && element.srcObject !== participant.stream) {
        element.srcObject = participant.stream;
      }
    });
  }

  private scrollChatToLatest(): void {
    setTimeout(() => {
      const container = this.chatStream?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }
}
